import { useState } from 'react';
import { useDebounce } from './useDebounce';
import { getTenant, isCurrentUserOnlyDepartmentManager } from './helpers';

const useArchivedAssignments = ({
  type = 'course',
  view = 'learner',
  search = '',
  page = 1,
  size = 10,
  platformKey = '',
  refresher = null,
}) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 0,
  });

  useDebounce(async () => {
    setLoading(true);

    const tenant = platformKey || getTenant();

    // Prepare query params matching working hook keys ('page_size' and 'page')
    const params = new URLSearchParams({
      platform_key: tenant,
      page_size: size,
      page,
      ...(search.trim() ? { query: search.trim() } : {}),
      ...(isCurrentUserOnlyDepartmentManager() ? { department_mode: 1 } : {}),
    }).toString();

    // Construct API endpoint URL using correct path segments similar to learners hook
    // Example: /api/catalog/suggestions/{type}/manage/archive or /group/archive
    const pathPart = view === 'group' ? 'group/archive' : 'archive';
    const baseUrl = process.env.REACT_APP_IBL_DM_URL; // unify env usage
    if (!baseUrl) {
      console.error('API base URL not set');
      setLoading(false);
      return;
    }
    const url = `${baseUrl}/api/catalog/suggestions/${type}/manage/${pathPart}?${params}`;

    try {
      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json', // align headers with other calls
          Authorization: `Token ${localStorage.getItem('dm_token')}`,
        },
      });

      if (!resp.ok) throw new Error('API responded with error: ' + resp.status);

      const data = await resp.json();

      setAssignments(Array.isArray(data.results) ? data.results : []);
      setPagination({
        totalItems: data.count || 0,
        currentPage: page,
        totalPages: Math.ceil((data.count || 0) / size),
        pageSize: size,
      });
    } catch (error) {
      console.error('Error fetching archived assignments:', error);
      setAssignments([]);
      setPagination({ totalItems: 0, totalPages: 0, currentPage: page, pageSize: size });
    } finally {
      setLoading(false);
    }
  }, 200, [search, page, size, type, view, platformKey, refresher]);

  return { assignments, loading, pagination };
};

export default useArchivedAssignments;
